/**
 * Why TaaS Page Component
 * Explains the benefits of TaaS over internal hiring with risk messaging and cost comparison
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import './WhyTaaS.css';

// Icons
const WarningIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);

const CheckCircleIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
);

function WhyTaaS() {
    return (
        <>
            <Helmet>
                <title>Why TaaS? | Internal Hiring vs CODEMAKER TaaS</title>
                <meta
                    name="description"
                    content="Discover why CODEMAKER's Talent-as-a-Service model is safer and more cost-effective than hiring internal developers. Compare costs, risks, and benefits."
                />
            </Helmet>

            <main>
                {/* Hero Section */}
                <section className="why-taas-hero">
                    <div className="why-taas-hero__container">
                        <span className="why-taas-hero__badge">Why TaaS with CODEMAKER</span>
                        <h1 className="why-taas-hero__title">
                            Why TaaS is Safer and Cheaper<br />Than Hiring In-House
                        </h1>
                        <p className="why-taas-hero__description">
                            Internal hiring requires deep domain knowledge, carries significant HR and compliance risks,
                            and is often far more expensive once the full cost of ownership is considered.
                        </p>
                        <div className="why-taas-hero__actions">
                            <Button as={Link} to="/contact" variant="primary" size="large">
                                Evaluate Your Team Cost
                            </Button>
                        </div>
                    </div>
                </section>

                {/* Common Challenges Section */}
                <Section className="risk-section">
                    <Section.Header
                        subtitle="The Reality"
                        title="Common Challenges of Internal Hiring"
                        description="Building an in-house team requires significant resources and technical expertise beyond just hiring developers."
                    />

                    <div className="risk-grid">
                        <div className="risk-card">
                            <div className="risk-card__icon-wrapper">
                                <WarningIcon />
                            </div>
                            <div className="risk-card__content">
                                <h3 className="risk-card__title">Technical Complexity</h3>
                                <ul className="risk-card__list">
                                    <li><strong>Evaluating technical talent</strong> requires deep domain knowledge.</li>
                                    <li>Ensuring <strong>code quality, scalability, and security</strong> requires experienced technical leadership.</li>
                                    <li>Mismatched hires can lead to project delays and rework.</li>
                                    <li>Technical debt can accumulate quickly without proper oversight.</li>
                                </ul>
                            </div>
                        </div>

                        <div className="risk-card">
                            <div className="risk-card__icon-wrapper">
                                <WarningIcon />
                            </div>
                            <div className="risk-card__content">
                                <h3 className="risk-card__title">Total Cost of Ownership</h3>
                                <ul className="risk-card__list">
                                    <li><strong>Salary is only part of the cost.</strong></li>
                                    <li>Additional expenses include EPF, SOCSO, EIS, HRD, training, and equipment.</li>
                                    <li>A "RM3,500 junior salary" often translates to a much higher true monthly cost.</li>
                                    <li><strong>Turnover costs</strong> (recruitment, onboarding, lost productivity) are borne entirely by the company.</li>
                                </ul>
                            </div>
                        </div>

                        <div className="risk-card" style={{ gridColumn: '1 / -1' }}>
                            <div className="risk-card__icon-wrapper">
                                <WarningIcon />
                            </div>
                            <div className="risk-card__content">
                                <h3 className="risk-card__title">Team Structure Requirements</h3>
                                <p style={{ marginBottom: '1rem', color: 'var(--color-neutral-600)', lineHeight: '1.6' }}>
                                    Successful projects require more than just developers. They need management and analysis.
                                </p>
                                <ul className="risk-card__list">
                                    <li>A complete internal team typically requires a Project Manager and a Technical Lead.</li>
                                    <li>These roles add significant overhead, potentially <strong>RM16,800 – RM33,000 per month</strong>.</li>
                                    <li>TaaS provides this necessary oversight structure as part of the service.</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </Section>

                {/* Comparison Table Section */}
                <Section>
                    <Section.Header
                        subtitle="Side-by-Side Comparison"
                        title="Internal Hiring vs CODEMAKER TaaS"
                        description="See the difference in cost, risk, and commitment."
                    />

                    <div className="comparison-table-wrapper">
                        <table className="comparison-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '20%' }}>Category</th>
                                    <th style={{ width: '40%' }}>Internal Hiring (per month)</th>
                                    <th style={{ width: '40%' }}>CODEMAKER TaaS</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><strong>Developer Cost</strong></td>
                                    <td>
                                        <strong>~RM5,500–15,000 per developer</strong>
                                        True cost after contributions & overhead
                                    </td>
                                    <td>
                                        <strong>RM4,600–10,200 per developer</strong>
                                        No HR or statutory cost on your side
                                    </td>
                                </tr>
                                <tr>
                                    <td><strong>PM / Leadership</strong></td>
                                    <td>
                                        <strong>RM16,800–33,000 per month</strong>
                                        PM + System Analyst / Team Lead
                                    </td>
                                    <td>
                                        <strong>Included</strong>
                                        Tech Lead oversight included in subscription
                                    </td>
                                </tr>
                                <tr>
                                    <td><strong>Recruitment Cost</strong></td>
                                    <td>
                                        <strong>High</strong>
                                        Job ads, agencies fees, interview time (~RM 1,000)
                                    </td>
                                    <td>
                                        <strong>Included</strong>
                                        We recruit, screen and match developers
                                    </td>
                                </tr>
                                <tr>
                                    <td><strong>Technical Interviewing</strong></td>
                                    <td>
                                        <strong>Requires Experts</strong>
                                        Needs in-house domain experts
                                    </td>
                                    <td>
                                        <strong>Handled by Us</strong>
                                        Handled by CODEMAKER’s technical team
                                    </td>
                                </tr>
                                <tr>
                                    <td><strong>HR & Compliance</strong></td>
                                    <td>
                                        <strong>Employer Managed</strong>
                                        EPF, SOCSO, EIS, HRD, leave, and labour law
                                    </td>
                                    <td>
                                        <strong>No HR Obligations</strong>
                                        We are the employer of record
                                    </td>
                                </tr>
                                <tr>
                                    <td><strong>Equipment Cost</strong></td>
                                    <td>
                                        <strong>Company Borne</strong>
                                        Laptops, tools, office space
                                    </td>
                                    <td>
                                        <strong>Minimal</strong>
                                        Our standard arrangement minimises infra handling
                                    </td>
                                </tr>
                                <tr>
                                    <td><strong>Training Cost</strong></td>
                                    <td>
                                        <strong>Ongoing Cost</strong>
                                        Training, certifications, ramp-up
                                    </td>
                                    <td>
                                        <strong>Included</strong>
                                        Continuous upskilling handled by CODEMAKER
                                    </td>
                                </tr>
                                <tr>
                                    <td><strong>Replacement Risk</strong></td>
                                    <td>
                                        <strong>2–3 Months Delay</strong>
                                        To rehire and ramp up
                                    </td>
                                    <td>
                                        <strong>Minimal Disruption</strong>
                                        Replacement handled by CODEMAKER
                                    </td>
                                </tr>
                                <tr>
                                    <td><strong>Commitment</strong></td>
                                    <td>
                                        <strong>Long-term</strong>
                                        Difficult to downsize quickly
                                    </td>
                                    <td>
                                        <strong>Flexible</strong>
                                        3–12+ months with predictable subscription
                                    </td>
                                </tr>
                                <tr>
                                    <td><strong>Risk Profile</strong></td>
                                    <td>
                                        <strong>High</strong>
                                        You bear full hiring, performance, and compliance risk
                                    </td>
                                    <td>
                                        <strong>Low</strong>
                                        Risk shared; performance managed with Tech Lead oversight
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </Section>

                {/* Safer Summary Section */}
                <Section background="light">
                    <div className="safer-summary">
                        <h2 style={{ fontSize: 'var(--font-size-3xl)', fontWeight: 'bold', marginBottom: 'var(--space-6)', color: 'var(--color-primary-900)' }}>
                            Why CODEMAKER TaaS Is Safer
                        </h2>

                        <div className="safer-list">
                            <div className="safer-item">
                                <CheckCircleIcon />
                                <span>No domain knowledge required from your side – we handle technical evaluation.</span>
                            </div>
                            <div className="safer-item">
                                <CheckCircleIcon />
                                <span>No HR, statutory, or compliance headaches.</span>
                            </div>
                            <div className="safer-item">
                                <CheckCircleIcon />
                                <span>Tech Lead oversight ensures architectural quality and good coding practices.</span>
                            </div>
                            <div className="safer-item">
                                <CheckCircleIcon />
                                <span>Bulk discounts and waived onboarding fees make it cheaper than internal hiring.</span>
                            </div>
                            <div className="safer-item" style={{ gridColumn: '1 / -1' }}>
                                <CheckCircleIcon />
                                <span>You can scale up or down based on project needs and budget.</span>
                            </div>
                        </div>

                        <div style={{ marginTop: 'var(--space-8)', display: 'flex', gap: 'var(--space-4)', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Button as={Link} to="/contact" variant="primary" size="large">
                                Schedule a Cost Comparison Session
                            </Button>
                            <Button as={Link} to="/contact" variant="outline" size="large">
                                Request a Custom TaaS Proposal
                            </Button>
                        </div>
                    </div>
                </Section>
            </main>
        </>
    );
}

export default WhyTaaS;
